/**
 * Admin Subscriptions Repository
 *
 * Manages viewing all subscriptions (active and canceled) for admin dashboard.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { post, put } from 'data/services/apiService';

// ============================================
// Create Repository
// ============================================
const adminSubscriptionsRepository = createRepository({
	endpoint: 'admin/subscriptions',
	name: 'adminSubscriptions',
	statePath: 'adminSubscriptions',

	// Custom methods for enterprise and admin operations
	customMethods: {
		/**
		 * Create enterprise subscription
		 * Usage: actions.createEnterprise({ user_id, property_limit, seat_limit, ... })
		 */
		createEnterprise: ({ data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/admin/subscriptions/enterprise`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Convert existing subscription to enterprise
		 * Usage: actions.convertToEnterprise({ subscriptionId, data })
		 */
		convertToEnterprise: ({ subscriptionId, data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/admin/subscriptions/${subscriptionId}/convert-to-enterprise`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Update enterprise subscription limits
		 * Usage: actions.updateEnterpriseLimits({ id, data })
		 */
		updateEnterpriseLimits: ({ id, data, authToken, dispatch }) => {
			return put({
				url: `${getApiUrl()}/api/admin/subscriptions/${id}/enterprise-limits`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Onboard enterprise client with complete setup
		 * Creates user, portfolio, assigns admin, creates subscription, sends email
		 * Usage: actions.onboardEnterprise({ data: { name, email, portfolio_name, subscription_type, ... } })
		 */
		onboardEnterprise: ({ data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/admin/onboarding/enterprise`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Onboard free trial client (for marketing/BVS purposes)
		 * Creates user, portfolio, disables notifications, sends free trial email
		 * Usage: actions.onboardFreeTrial({ data: { name, email, portfolio_name, ... } })
		 */
		onboardFreeTrial: ({ data, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/admin/onboarding/free-trial`,
				data,
				authToken,
				dispatch,
			});
		},
	},
});

// ============================================
// Exports
// ============================================

/**
 * Available in hook (actions object):
 *
 * Standard CRUD:
 * - fetchAll(params)                    - Fetch all without pagination
 * - fetchPaginated(page, perPage, params) - Fetch with pagination (supports status filter)
 * - fetchOne(id)                        - Fetch single subscription
 * - reset()                             - Clear state
 * - clearErrors()                       - Clear errors
 *
 * Custom Methods:
 * - createEnterprise(data)              - Create enterprise subscription
 * - convertToEnterprise(subscriptionId, data) - Convert to enterprise
 * - updateEnterpriseLimits(id, data)    - Update limits
 * - onboardEnterprise(data)             - Complete enterprise onboarding
 * - onboardFreeTrial(data)              - Complete free trial onboarding
 *
 * Example with status filter:
 * actions.fetchPaginated(1, 15, { status: 'active' })
 * actions.fetchPaginated(1, 15, { status: 'canceled' })
 * actions.fetchPaginated(1, 15, { status: 'all' })
 */
export const {
	name,
	statePath,
	thunks: adminSubscriptionsThunks,
	reducer: adminSubscriptionsReducer,
	actions: adminSubscriptionsActions,
	types: adminSubscriptionsTypes,
	selectors: adminSubscriptionsSelectors,
	useHook: useAdminSubscriptions, // 🎉 The magic hook!
} = adminSubscriptionsRepository;

// Export repository for advanced usage
export default adminSubscriptionsRepository;
